import React, { useState, useEffect } from "react";
import "./Dashboard.css";

// Map demo chef names to Cloudflare Images URLs
const DEMO_CHEFS = [
  { name: "Chef Maria", img: "https://imagedelivery.net/YQhisQdXT8651Bk9Z7c9KQ/31c41e57-f5fa-40b6-16e6-f26abdd7c700/public" }, // chef1
  { name: "Chef David", img: "https://imagedelivery.net/YQhisQdXT8651Bk9Z7c9KQ/1011e93f-269d-47da-5c97-b53bce4eb600/public" }, // chef2
  { name: "Chef James", img: "https://imagedelivery.net/YQhisQdXT8651Bk9Z7c9KQ/b823c9c5-a3bf-42f0-ded1-240a4b8f4500/public" }, // chef3
  { name: "Chef Sarah", img: "https://imagedelivery.net/YQhisQdXT8651Bk9Z7c9KQ/6bf5fd8a-c9ea-47e8-a965-15c284ff5a00/public" }, // chef4
  { name: "Chef Lisa", img: "https://imagedelivery.net/YQhisQdXT8651Bk9Z7c9KQ/c6d67a3c-84d9-42ef-4676-031fd4233e00/public" }, // chef5
  { name: "Chef Michael", img: "https://imagedelivery.net/YQhisQdXT8651Bk9Z7c9KQ/58756b2a-9dc0-4097-9fb0-c3217f80dd00/public" }, // chef6
  { name: "Chef Emma", img: "https://imagedelivery.net/YQhisQdXT8651Bk9Z7c9KQ/f3134b26-c016-4b9a-2809-8d8e332ba400/public" }, // chef7
  { name: "Chef Robert", img: "https://imagedelivery.net/YQhisQdXT8651Bk9Z7c9KQ/b2e3b402-3660-4e3e-23aa-cfb693b16d00/public" }, // chef8
  { name: "Chef Anna", img: "https://imagedelivery.net/YQhisQdXT8651Bk9Z7c9KQ/dc110e42-bc52-48d2-a47b-cc46b5ccd700/public" }, // chef9
  { name: "Chef Chris", img: "https://imagedelivery.net/YQhisQdXT8651Bk9Z7c9KQ/73c31bd5-c860-44e3-4827-133779e10a00/public" }, // chef10
];

// Placeholder image from Cloudflare Images
const PLACEHOLDER_URL = "https://imagedelivery.net/YQhisQdXT8651Bk9Z7c9KQ/b459f40c-612e-478e-2210-e2ec4e600400/public";

const CITIES = [
  { id: 'toronto', name: 'Toronto' },
  { id: 'new-york', name: 'New York' },
  { id: 'boston', name: 'Boston' },
  { id: 'chicago', name: 'Chicago' },
  { id: 'new-orleans', name: 'New Orleans' },
  { id: 'miami', name: 'Miami' },
  { id: 'austin', name: 'Austin' },
  { id: 'dallas', name: 'Dallas' },
  { id: 'houston', name: 'Houston' }
];

export default function ChefWidget() {
  const [selectedCity, setSelectedCity] = useState('toronto');
  const [chefs, setChefs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadChefs();
  }, [selectedCity]);

  const loadChefs = async () => {
    try {
      setLoading(true);
      
      // Load chefs from specific city with hybrid system (7 real + 1 placeholder = 8 total)
      try {
        const response = await fetch(`/api/chefs?city=${selectedCity}&status=approved,live`);
        const data = await response.json();
        const cityChefs = data.results || [];
        
        // Create hybrid system: 7 real chefs + 1 placeholder = 8 total
        const hybridChefs = createHybridChefList(cityChefs, selectedCity);
        setChefs(hybridChefs);
      } catch (error) {
        console.error('Error loading chefs:', error);
        // If API fails, create 8 placeholder chefs
        const placeholderChefs = createPlaceholderChefs(selectedCity);
        setChefs(placeholderChefs);
      }
    } catch (error) {
      console.error('Error loading chefs:', error);
      setChefs([]);
    } finally {
      setLoading(false);
    }
  };

  const createHybridChefList = (realChefs, cityId) => {
    // Always create exactly 8 chefs total (7 real/demo + 1 placeholder)
    const allChefs = [];
    
    // Add real chefs first
    const selectedRealChefs = realChefs.slice(0, 7);
    allChefs.push(...selectedRealChefs);
    
    // Fill remaining slots with demo chefs if needed
    const cityName = CITIES.find(c => c.id === cityId)?.name || cityId;
    const demoChefNames = [
      'Chef Maria', 'Chef David', 'Chef Sarah', 'Chef James', 'Chef Lisa', 
      'Chef Michael', 'Chef Emma', 'Chef Robert', 'Chef Anna', 'Chef Chris'
    ];
    
    // Shuffle demo chef names for randomization
    const shuffledNames = [...demoChefNames].sort(() => Math.random() - 0.5);
    
    while (allChefs.length < 7) {
      const demoIndex = allChefs.length;
      const demoChef = {
        id: `demo-${cityId}-${demoIndex}`,
        name: shuffledNames[demoIndex % shuffledNames.length],
        city: cityId,
        photo_url: null, // This will trigger demo image assignment
        status: 'available',
        isDemo: true
      };
      allChefs.push(demoChef);
    }
    
    // Add one more demo chef to make exactly 8 chefs total
    if (allChefs.length < 8) {
      const demoIndex = allChefs.length;
      const demoChef = {
        id: `demo-${cityId}-${demoIndex}`,
        name: shuffledNames[demoIndex % shuffledNames.length],
        city: cityId,
        photo_url: null, // This will trigger random image assignment
        status: 'available',
        isDemo: true
      };
      allChefs.push(demoChef);
    }
    
    // Shuffle the final array to randomize positions
    return allChefs.sort(() => Math.random() - 0.5);
  };

  const createPlaceholderChefs = (cityId) => {
    // Create 8 placeholder chefs if no real data
    const placeholders = [];
    for (let i = 1; i <= 8; i++) {
      placeholders.push({
        id: `placeholder-${cityId}-${i}`,
        name: `Chef Available ${i}`,
        city: cityId,
        photo_url: null,
        status: 'available',
        isPlaceholder: true
      });
    }
    return placeholders;
  };

  const getStatusBadge = (status) => {
    const badges = {
      'approved': { text: 'Active', class: 'status-active' },
      'live': { text: 'Live', class: 'status-live' },
      'pending': { text: 'Pending', class: 'status-pending' },
      'rejected': { text: 'Rejected', class: 'status-rejected' }
    };
    const badge = badges[status] || { text: status, class: 'status-unknown' };
    return <span className={`status-badge ${badge.class}`}>{badge.text}</span>;
  };

  const getChefImage = (chef, cityId) => {
    // If chef has a real photo, use it (from R2 storage)
    if (chef.photo_url) {
      return chef.photo_url;
    }
    
    // For demo chefs, find the specific Cloudflare Images URL for this chef name
    const demoChef = DEMO_CHEFS.find(dc => dc.name === chef.name);
    if (demoChef) {
      return demoChef.img;
    }
    
    // Fallback to placeholder from Cloudflare Images
    return PLACEHOLDER_URL;
  };

  const generateDummyPhone = (chef) => {
    // Generate consistent phone number based on chef name
    let hash = 0;
    const chefName = chef.name || `chef-${chef.id}`;
    for (let i = 0; i < chefName.length; i++) {
      hash = (hash * 31 + chefName.charCodeAt(i)) >>> 0;
    }
    
    // Generate phone number: (555) XXX-XXXX
    const areaCode = 555;
    const exchange = 200 + (hash % 800); // 200-999
    const number = 1000 + (hash % 9000); // 1000-9999
    
    return `(${areaCode}) ${exchange}-${number}`;
  };

  const generateDummyEmail = (chef) => {
    // Generate email like ana@chefs2table.com
    const chefName = chef.name || `chef-${chef.id}`;
    const firstName = chefName.toLowerCase().replace(/[^a-z]/g, '').split(' ')[0];
    return `${firstName}@chefs2table.com`;
  };

  const getChefsKitchen = (chef) => {
    // Generate consistent kitchen number based on chef name
    let hash = 0;
    const chefName = chef.name || `chef-${chef.id}`;
    for (let i = 0; i < chefName.length; i++) {
      hash = (hash * 31 + chefName.charCodeAt(i)) >>> 0;
    }
    
    return `Kitchen ${(hash % 20) + 1}`; // Kitchens 1-20
  };

  return (
    <div className="widget">
      <div className="widget-header">
        <h2 className="widget-title" style={{ color: 'rgba(130, 67, 67, 1)' }}>Chefs</h2>
        <div className="city-pills">
          {CITIES.map(city => (
            <button
              key={city.id}
              className={`city-pill ${selectedCity === city.id ? 'active' : ''}`}
              onClick={() => setSelectedCity(city.id)}
            >
              {city.name}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading chefs...</div>
      ) : (
        <div className="chefs-row">
          {chefs.length === 0 ? (
            <div className="no-data">
              No chefs found for {CITIES.find(c => c.id === selectedCity)?.name}
            </div>
          ) : (
            chefs.map((chef, index) => (
              <div key={chef.id} className="chef-card">
                <div className="chef-avatar">
                  <img 
                    src={getChefImage(chef, selectedCity)} 
                    alt={chef.name}
                    className="chef-image"
                  />
                </div>
                <div className="chef-details">
                  <div className="chef-name">{chef.name}</div>
                  <div className="chef-city">{chef.city}</div>
                  <div className="chef-kitchen">{getChefsKitchen(chef)}</div>
                  <div className="chef-phone">{generateDummyPhone(chef)}</div>
                  <div className="chef-email">{generateDummyEmail(chef)}</div>
                  {getStatusBadge(chef.status)}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
